import { DecimalPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormsModule, NgModel, ValidationErrors } from '@angular/forms';
import { MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatCell, MatCellDef, MatColumnDef, MatFooterCell, MatFooterCellDef, MatFooterRow, MatFooterRowDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { EigenleistungItem } from '@gsm/entities';
import { WINDOW } from 'ngx-window-token';
import { compareProps } from '../../helpers/compare-props.helper';
import { filterEmptyProps } from '../../helpers/filter-props.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { EigenleistungTableModel } from './eigenleistung-table.model';

@Component({
  selector:    'gsm-eigenleistung-table',
  templateUrl: './eigenleistung-table.component.html',
  styleUrls:   ['./eigenleistung-table.component.scss'],
  standalone:  true,
  imports:     [
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFormField,
    NgIf,
    MatSelect,
    FormsModule,
    NgFor,
    MatOption,
    MatFooterCellDef,
    MatFooterCell,
    MatCheckbox,
    NgClass,
    MatInput,
    MatIconButton,
    MatIcon,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    MatMiniFabButton,
    DecimalPipe
  ]
})
export class EigenleistungTableComponent extends CustomDynamicFormControlComponent<EigenleistungTableModel> implements OnInit {

  private window = inject<Window>(WINDOW);

  numberPattern = '^(0|[1-9][0-9]*)$';
  displayedColumns: string[] = [
    'gewerk',
    'kv',
    'matKosten',
    'arbeitsStd',
    'arbeitsStdKosten',
    'qm',
    'qmKosten',
    'summe',
    'delete'
  ];
  itemList = new MatTableDataSource<EigenleistungItem>();
  gewerkeCompareWithFn = compareProps.bind(null, ['value']);

  private _gewerkModels: QueryList<NgModel>;
  @ViewChildren('gewerk')
  set gewerkModels(models: QueryList<NgModel>) {
    this._gewerkModels = models;
    if (this.model.additional?.immediateValidation) {
      this._markRequiredFieldsTouched();
    }
  }

  get gewerkModels(): QueryList<NgModel> {
    return this._gewerkModels;
  }

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  onModelChange() {
    if (!this.model?.value?.positionen?.length) {
      this.itemList.data = [];
      this.addItem();
    } else {
      this.itemList.data = this.model.value.positionen;
    }
    this.window.setTimeout(() => {
      this._emitChange();
    }, 0);
  }

  private _updateRowSum(item: EigenleistungItem) {
    const matCost = item.materialkosten || 0;
    const arbeitsStd = item.arbeitsstunden || 0;
    const arbeitsStdKosten = item.stundensatz || 0;
    const qm = item.anzahlQm || 0;
    const qmKosten = item.kostenProQm || 0;
    item.summe = matCost + arbeitsStd * arbeitsStdKosten + qm * qmKosten;
  }

  get totalSum(): number {
    return this.itemList.data.reduce((total, item) => {
      const summe = item.summe || 0;
      return total + summe;
    }, 0);
  }

  addItem() {
    const newElItem: EigenleistungItem = {
      gewerk:         null,
      kvAbloese:      false,
      materialkosten: null,
      arbeitsstunden: null,
      stundensatz:    null,
      anzahlQm:       null,
      kostenProQm:    null,
      summe:          null
    };
    const data = this.itemList.data;
    data.push(newElItem);
    this.itemList.data = data;
    this.onFormValueChange();
  }

  deleteItem(index: number) {
    if (this.itemList.data.length > 1) {
      const data = this.itemList.data;
      data.splice(index, 1);
      this.itemList.data = data;
      this.onFormValueChange();
    } else if (index === 0) {
      this.resetItem(this.itemList.data[0], []);
    }
  }

  onInputBlur(item: EigenleistungItem, event?: FocusEvent) {
    if (event) {
      const target = event.target as HTMLInputElement;
      if (target.name === 'arbeitsStd' && target.value === '') {
        item.stundensatz = null;
      } else if (target.name === 'qm' && target.value === '') {
        item.kostenProQm = null;
      }
    }

    this._updateRowSum(item);
    this.onFormValueChange();
  }

  resetItem(item: EigenleistungItem, except = [
    'kvAbloese',
    'gewerk',
    'summe'
  ]) {
    item.materialkosten = null;
    item.arbeitsstunden = null;
    item.stundensatz = null;
    item.anzahlQm = null;
    item.kostenProQm = null;
    [
      'kvAbloese',
      'gewerk',
      'summe'
    ].filter(prop => !except.includes(prop))
      .forEach(prop => (item[prop] = null));
    this.onFormValueChange();
  }

  onFormValueChange() {
    this.model.value = {
      positionen: this.itemList.data.map(item => filterEmptyProps(item)),
      summe:      this.totalSum
    };
    this._markRequiredFieldsTouched();
    this._emitChange();
  }

  private _markRequiredFieldsTouched() {
    if (this.gewerkModels) {
      this.gewerkModels.forEach(model => model.control.markAsTouched());
    }
  }

  private _emitChange() {
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors(): ValidationErrors {
    let invalid = true;
    const gewerkIsInvalid = !!this.itemList.data.find(item => item && item.gewerk === null);
    const rowSumIsInvalid = !!this.itemList.data.find(item => item && (item.summe === null || item.summe === 0));
    if (this.totalSum > 0 && !gewerkIsInvalid && !rowSumIsInvalid) {
      invalid = false;
    }
    return invalid ? {eigenleistungAufstellung: true} : null;
  }
}
